import { type CSSProperties, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { H5 } from "simplydo/react/ui/header";
import { Input } from "simplydo/react/ui/input";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

export type DiscussionPoll = {
  _id: string;
  title: string;
  allowMultiSelect: boolean;
  showVoters?: boolean;
  closed?: boolean;
  hasVoted?: boolean;
  myResponseOptionIds?: string[];
  totalVotes?: number;
  options: Array<{
    _id: string;
    title: string;
    voteCount?: number;
    percentage?: number;
    voters?: OpenAPI.Schemas["User"][];
  }>;
};

type DiscussionPollsProps = {
  commentId: string;
  polls: DiscussionPoll[];
  disabled?: boolean;
  voteOnPoll: (
    commentId: string,
    pollId: string,
    optionIds: string[],
  ) => Promise<void>;
  setPollClosed: (
    commentId: string,
    pollId: string,
    closed: boolean,
  ) => Promise<void>;
  canClosePoll: boolean;
};

const PollContainer = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 10px;
  margin-top: 8px;
`;

const ResultBarOuter = styled.div`
  height: 8px;
  background-color: #ececec;
  border-radius: 999px;
  overflow: hidden;
  width: 100%;
`;

const ResultBarInner = styled.div<{ $width: number }>`
  height: 100%;
  width: ${({ $width }) => `${$width}%`};
  background-color: #4a77d4;
  transition: width 0.2s ease-in-out;
  border-radius: 999px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
`;

const OptionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.92em;
`;

const ResultBarRow = styled.div`
  margin-top: 4px;
  margin-bottom: 8px;
  margin-left: 22px;
`;

const OptionPercent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 42px;
  color: #6a6a6a;
  font-size: 0.85em;
`;

const OptionPercentResults = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 20px;
`;

const OptionPercentValue = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 20px;
  white-space: nowrap;
`;

const PollVisibilityLabel = styled.div`
  color: #6a6a6a;
  font-size: 0.85em;
  line-height: 1em;
  margin-top: 4px;
`;

const PollMeta = styled.div`
  color: #6a6a6a;
  font-size: 0.85em;
  line-height: 1em;
`;

const VoterSummary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0;
  margin: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
  vertical-align: middle;
  appearance: none;
`;

const VoterAvatarWrap = styled.span<{ $size?: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size = 20 }) => $size}px;
  height: ${({ $size = 20 }) => $size}px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  box-sizing: border-box;

  .ui.avatar.image,
  img {
    width: ${({ $size = 20 }) => $size}px !important;
    height: ${({ $size = 20 }) => $size}px !important;
    min-width: ${({ $size = 20 }) => $size}px !important;
    margin: 0 !important;
  }
`;

const VoterOthersText = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
  line-height: 20px;
  white-space: nowrap;
`;

const VoterModalMeta = styled.div`
  color: #6a6a6a;
  font-size: 0.9em;
  margin-top: 16px;
`;

const VoterModalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
  max-height: 320px;
  overflow-y: auto;
`;

const VoterModalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

type VoterAvatarProps = {
  user: OpenAPI.Schemas["User"];
  size?: number;
  style?: CSSProperties;
};

const VoterAvatar = ({ user, size = 20, style }: VoterAvatarProps) => (
  <VoterAvatarWrap $size={size} style={style}>
    <ImageWithFallback
      avatar
      src={util.avatarUrl(user)}
      fallbackSrc={util.avatarUrl()}
      alt={user.profile?.fullName || "User"}
      style={{
        width: size,
        height: size,
        minWidth: size,
        objectFit: "cover",
        display: "block",
      }}
    />
  </VoterAvatarWrap>
);

const getInitialSelections = (polls: DiscussionPoll[]) => {
  const selections: Record<string, string[]> = {};
  for (const poll of polls) {
    selections[poll._id] = poll.myResponseOptionIds || [];
  }
  return selections;
};

type PollVotersProps = {
  voters: OpenAPI.Schemas["User"][];
  optionTitle: string;
};

const PollVoters = ({ voters, optionTitle }: PollVotersProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredVoters = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return voters;
    }
    return voters.filter((voter) =>
      (voter.profile?.fullName || "").toLowerCase().includes(query),
    );
  }, [searchTerm, voters]);

  if (!voters.length) {
    return null;
  }

  const visibleVoters = voters.slice(0, 5);
  const remainingCount = voters.length - visibleVoters.length;

  return (
    <>
      <VoterSummary
        type="button"
        onClick={() => {
          setSearchTerm("");
          setOpen(true);
        }}
        aria-label={`View voters for ${optionTitle}`}
      >
        {visibleVoters.map((voter, index) => (
          <VoterAvatar
            key={voter._id}
            user={voter}
            style={{
              marginLeft: index === 0 ? 0 : -6,
              border: "1px solid #fff",
            }}
          />
        ))}
        {remainingCount > 0 ? (
          <VoterOthersText>
            + {util.pluralise(remainingCount, "other", "others", true)}
          </VoterOthersText>
        ) : null}
      </VoterSummary>

      <Modal open={open} onClose={() => setOpen(false)} size="small">
        <ModalHeader>{optionTitle} voters</ModalHeader>
        <ModalContent>
          <Input
            fluid

            iconPosition="left"
            placeholder="Search voters"
            value={searchTerm}
            onChange={(_event, data) => setSearchTerm(String(data.value || ""))}
          />
          <VoterModalMeta>
            {util.pluralise(voters.length, "voter", "voters", true)}
          </VoterModalMeta>
          <VoterModalList>
            {filteredVoters.map((voter) => (
              <VoterModalRow key={voter._id}>
                <UserChip user={voter} />
              </VoterModalRow>
            ))}
            {!filteredVoters.length ? (
              <Message size="small">No voters match your search.</Message>
            ) : null}
          </VoterModalList>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export const DiscussionPolls = ({
  commentId,
  polls,
  disabled = false,
  voteOnPoll,
  setPollClosed,
  canClosePoll,
}: DiscussionPollsProps) => {
  const { t } = useTranslation();
  const [selectionState, setSelectionState] = useState<
    Record<string, string[]>
  >(() => getInitialSelections(polls));
  const [savingPollIds, setSavingPollIds] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    setSelectionState(getInitialSelections(polls));
  }, [polls]);

  if (!polls.length) {
    return null;
  }

  const savePollVote = async (poll: DiscussionPoll, optionIds: string[]) => {
    setSavingPollIds((prev) => ({ ...prev, [poll._id]: true }));
    try {
      await voteOnPoll(commentId, poll._id, optionIds);
    } catch (_error) {
      setSelectionState(getInitialSelections(polls));
    } finally {
      setSavingPollIds((prev) => ({ ...prev, [poll._id]: false }));
    }
  };

  const updateDiscussionPollState = async (
    poll: DiscussionPoll,
    closed: boolean,
  ) => {
    setSavingPollIds((prev) => ({ ...prev, [poll._id]: true }));
    try {
      await setPollClosed(commentId, poll._id, closed);
    } finally {
      setSavingPollIds((prev) => ({ ...prev, [poll._id]: false }));
    }
  };

  return (
    <>
      {polls.map((poll) => {
        const selectedOptionIds =
          selectionState[poll._id] || poll.myResponseOptionIds || [];
        const isClosed = !!poll.closed;
        const canSeePollVotes = poll.totalVotes !== undefined;
        const totalVotes =
          poll.totalVotes ??
          (poll.options || []).reduce(
            (total, option) => total + (option.voteCount || 0),
            0,
          );
        const isSaving = !!savingPollIds[poll._id];
        const showResults =
          isClosed ||
          (poll.options || []).some((option) => (option.percentage ?? 0) > 0);
        const pollInteractionsDisabled = disabled || isSaving || isClosed;

        const updateSelection = (optionId: string) => {
          if (pollInteractionsDisabled) {
            return;
          }

          const isSelected = selectedOptionIds.includes(optionId);
          const nextSelections = poll.allowMultiSelect
            ? isSelected
              ? selectedOptionIds.filter((id) => id !== optionId)
              : [...selectedOptionIds, optionId]
            : isSelected
              ? []
              : [optionId];

          setSelectionState((prev) => ({
            ...prev,
            [poll._id]: nextSelections,
          }));
          void savePollVote(poll, nextSelections);
        };

        return (
          <PollContainer key={poll._id}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <H5 style={{ marginBottom: 0 }}>{poll.title}</H5>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  {canSeePollVotes ? (
                    <PollMeta>
                      {totalVotes}{" "}
                      {totalVotes === 1 ? "person has" : "people have"} voted
                    </PollMeta>
                  ) : null}
                  <PollVisibilityLabel>
                    {poll.showVoters
                      ? t("discussion.polls.votesVisible")
                      : t("discussion.polls.votesAnonymous")}
                  </PollVisibilityLabel>
                </div>
              </div>

              {canClosePoll ? (
                <Button
                  size="small"
                  loading={isSaving}
                  disabled={pollInteractionsDisabled}
                  onClick={() => updateDiscussionPollState(poll, !isClosed)}
                  variant="outline"
                >
                  {isClosed ? "Reopen poll" : "Close poll"}
                </Button>
              ) : null}
            </div>

            <div style={{ marginBottom: 12 }}>
              {(poll.options || []).map((option) => {
                const selected = selectedOptionIds.includes(option._id);
                const showVoteCount = option.voteCount !== undefined;

                return (
                  <div key={option._id}>
                    <OptionRow>
                      <OptionLabel>
                        <Checkbox
                          type={poll.allowMultiSelect ? "checkbox" : "radio"}
                          checked={selected}
                          disabled={pollInteractionsDisabled}
                          onClick={() => updateSelection(option._id)}
                        >
                          {option.title}
                        </Checkbox>
                      </OptionLabel>

                      <OptionPercent>
                        {showResults ? (
                          <OptionPercentResults>
                            <OptionPercentValue>
                              {option.percentage || 0}%
                              {option.voteCount && ` (${option.voteCount})`}
                            </OptionPercentValue>
                            {showVoteCount && option.voters?.length ? (
                              <PollVoters
                                voters={option.voters}
                                optionTitle={option.title}
                              />
                            ) : null}
                          </OptionPercentResults>
                        ) : (
                          <span>&nbsp;</span>
                        )}
                      </OptionPercent>
                    </OptionRow>
                    <ResultBarRow>
                      <ResultBarOuter>
                        <ResultBarInner
                          $width={showResults ? option.percentage || 0 : 0}
                        />
                      </ResultBarOuter>
                    </ResultBarRow>
                  </div>
                );
              })}
            </div>
            {isClosed ? (
              <Message size="small" style={{ marginTop: 8, marginBottom: 0 }}>
                This poll is closed.
              </Message>
            ) : null}
          </PollContainer>
        );
      })}
    </>
  );
};
